import React, { useState, useRef } from "react";
import { McFileUpload } from "@maersk-global/mds-react-wrapper/components-core/mc-file-upload";
import { McButton } from "@maersk-global/mds-react-wrapper/components-core/mc-button";
import { McTable } from "@maersk-global/mds-react-wrapper/components-core/mc-table";
import { McPagination } from "@maersk-global/mds-react-wrapper/components-core/mc-pagination";
import { McModal } from "@maersk-global/mds-react-wrapper/components-core/mc-modal";

/**
 * BulkReassign Component
 * Handles bulk reassignment of approvers and PO creators
 */
function BulkReassign({ token, showToast, onUploadDone }) {
    const [excelFile, setExcelFile] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [progress, setProgress] = useState(null);
    const excelFileRef = useRef(null);

    const handleFileChange = (e) => {
        const file = e?.detail?.[0];
        if (file) {
            excelFileRef.current = file;
            setExcelFile(file);
        }
    };

    const handleUpload = async () => {
        const fileToUpload = excelFileRef.current;
        if (!fileToUpload) {
            showToast("Please select an Excel file before uploading", "error");
            return;
        }

        const formData = new FormData();
        formData.append("file", fileToUpload);

        setUploading(true);
        setProgress(null);
        showToast("Uploading and validating reassignment data...", "info");

        const accessToken = token || localStorage.getItem(process.env.REACT_APP_CIAM_ACCESS_KEY);
        if (!accessToken) {
            showToast("Authentication token not found", "error");
            setUploading(false);
            return;
        }

        const endpoint = `${process.env.REACT_APP_API_BASE_URL}/bulk-reassign`;

        try {
            const response = await fetch(endpoint, {
                method: "POST",
                headers: {
                    "Authorization": `Bearer ${accessToken}`,
                    "consumer-key": process.env.REACT_APP_FORGEROCK_CONSUMER_KEY
                },
                body: formData
            });

            // Handle error responses
            if (!response.ok) {
                let errorData;
                try {
                    errorData = await response.json();
                } catch {
                    errorData = {};
                }

                // Handle validation errors from backend (422 status)
                if (response.status === 422 && errorData.detail?.validation_errors) {
                    const validationErrors = errorData.detail.validation_errors;
                    const errorMsg = errorData.detail?.message || "Validation failed. Please check the errors below.";
                    showToast(errorMsg, "error");
                    
                    // Format validation errors for display
                    const formattedErrors = validationErrors.map(err => ({
                        row: err.row,
                        parent_id: err.parent_id || "",
                        child_id: err.child_id || "",
                        status: "error",
                        message: err.errors?.join("; ") || "Unknown error"
                    }));

                    onUploadDone(
                        {
                            total_rows: validationErrors.length,
                            valid_rows: 0,
                            success_count: 0,
                            error_count: validationErrors.length
                        },
                        formattedErrors
                    );
                    return;
                }

                // Generic error handling
                const errorMsg = errorData.error || errorData.detail || errorData.message || `Upload failed (HTTP ${response.status})`;
                showToast(errorMsg, "error");
                return;
            }

            // Success response
            const data = await response.json();

            if (data.status === "completed") {
                const summary = {
                    total_rows: data.total_records || 0,
                    valid_rows: data.total_records || 0,
                    success_count: data.success_count || 0,
                    error_count: data.error_count || 0
                };

                onUploadDone(summary, data.results || []);

                if (data.error_count === 0) {
                    showToast(`Successfully reassigned ${data.success_count} record(s)!`, "success");
                } else {
                    showToast(`Completed: ${data.success_count} success, ${data.error_count} errors`, "warning");
                }
            } else {
                showToast(data.message || "Upload failed", "error");
            }
        } catch (error) {
            showToast(error.message || "Upload failed", "error");
            console.error("Reassign upload error:", error);
        } finally {
            setUploading(false);
            setProgress(null);
        }
    };

    const btnLabel = uploading ? "Processing..." : "Upload & Process";

    return (
        <div className="bulk-upload-single">
            <div className="bulk-card">
                <h4 className="bulk-card-title">Upload Bulk Reassign Excel File</h4>
                <p style={{ fontSize: "0.9rem", color: "#666", marginTop: "8px" }}>
                    <strong>Note:</strong> You can reassign approvers, PO creators, or both in a single upload. Provide complete pairs of old and new email addresses.
                </p>

                <div className="bulk-file-upload-area">
                    <McFileUpload
                        label="Select Excel File"
                        accept=".xlsx,.xls"
                        input={handleFileChange}
                    />
                </div>

                {excelFile && (
                    <div className="bulk-file-info">
                        <div className="bulk-file-info-row">
                            <span className="bulk-file-info-label">File</span>
                            <span className="bulk-file-info-value">{excelFile.name}</span>
                        </div>
                        <div className="bulk-file-info-row">
                            <span className="bulk-file-info-label">Size</span>
                            <span className="bulk-file-info-value">{(excelFile.size / 1024).toFixed(1)} KB</span>
                        </div>
                    </div>
                )}

                <div className="bulk-upload-action">
                    <McButton
                        onClick={handleUpload}
                        appearance="primary"
                        disabled={!excelFile || uploading}
                    >
                        {btnLabel}
                    </McButton>
                </div>

                {uploading && (
                    <div className="bulk-progress">
                        <div className="bulk-progress-initial">
                            <div className="loader"></div>
                            <p className="bulk-progress-text">Validating and processing reassignment data...</p>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

/**
 * Formats an updated_fields object (e.g. { Event_Approver__c: "a@x.com,b@x.com" })
 * into a readable multi-pair string for table display.
 */
function formatUpdatedFields(updatedFields) {
    if (!updatedFields || typeof updatedFields !== "object" || Object.keys(updatedFields).length === 0) {
        return "-";
    }
    return Object.entries(updatedFields)
        .map(([field, value]) => `${field}: ${value}`)
        .join("  |  ");
}

/**
 * BulkReassignResults Component
 * Displays results from bulk reassignment operation
 */
function BulkReassignResults({ summary, results, onReset, onDownload }) {
    const columns = [
        { id: "row", label: "Row", noWrap: true },
        { id: "parent_id", label: "Parent ID", noWrap: true },
        { id: "child_id", label: "Child ID", noWrap: true },
        { id: "status", label: "Status", noWrap: true },
        { id: "updated_fields", label: "Updated Fields", width: "320px" },
        { id: "message", label: "Message", width: "320px" }
    ];

    const tableData = results.map((r, i) => ({
        id: i,
        row: r.row,
        parent_id: r.parent_id,
        child_id: r.child_id,
        status: r.status,
        updated_fields: formatUpdatedFields(r.updated_fields),
        message: r.message
    }));

    return (
        <div>
            {/* Summary cards */}
            <div className="bulk-summary-grid">
                <div className="bulk-summary-card bulk-summary-total">
                    <div className="bulk-summary-value">{summary.total_rows}</div>
                    <div className="bulk-summary-label">Total Rows</div>
                </div>
                <div className="bulk-summary-card bulk-summary-valid">
                    <div className="bulk-summary-value">{summary.valid_rows}</div>
                    <div className="bulk-summary-label">Valid Rows</div>
                </div>
                <div className="bulk-summary-card bulk-summary-success">
                    <div className="bulk-summary-value">{summary.success_count}</div>
                    <div className="bulk-summary-label">Successful</div>
                </div>
                <div className="bulk-summary-card bulk-summary-error">
                    <div className="bulk-summary-value">{summary.error_count}</div>
                    <div className="bulk-summary-label">Errors</div>
                </div>
            </div>

            {/* Action buttons */}
            <div style={{ display: "flex", gap: "12px", marginBottom: "20px", justifyContent: "flex-end" }}>
                <McButton onClick={onDownload} label="Download Results" appearance="secondary" fit="small" />
                <McButton onClick={onReset} label="New Upload" appearance="primary" fit="small" />
            </div>

            {/* Detailed results table */}
            <div className="bulk-table-wrapper">
                <h4 className="bulk-card-title" style={{ marginBottom: "16px" }}>Detailed Results</h4>
                <div style={{
                    border: "1px solid #e0e0e0",
                    borderRadius: "8px",
                    overflow: "hidden",
                    height: results.length > 10 ? "480px" : "auto",
                    display: "flex",
                    flexDirection: "column"
                }}>
                    <div style={{ flex: 1, overflowY: results.length > 10 ? "auto" : "visible", minHeight: 0 }}>
                        <McTable
                            data={tableData}
                            datakey="id"
                            columns={columns.map(col => ({
                                ...col,
                                cellTemplate: col.id === "status"
                                    ? (e) => {
                                        const val = e.rowData.status || "";
                                        const ok = val.includes("success");
                                        const appearance = ok ? "success" : "error";
                                        const label = ok ? "Success" : "Error";
                                        return e.html`<mc-tag label="${label}" appearance="${appearance}"></mc-tag>`;
                                    }
                                    : undefined
                            }))}
                            zebrastripes
                            headersticky
                            fit="small"
                            height="auto"
                            recordsperpage={tableData.length || 25}
                            style={{ border: "none" }}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
}

/**
 * BulkReassignHistory Component
 * Displays paginated run-level history of past /bulk-reassign uploads
 */
function BulkReassignHistory({ history, loading, page, totalPages, totalRecords, onPageChange }) {
    const [detailRecord, setDetailRecord] = useState(null);

    const formatDate = (isoString) => {
        if (!isoString) return "-";
        const utcTimestamp = isoString.endsWith("Z") ? isoString : isoString + "Z";
        const date = new Date(utcTimestamp);
        if (isNaN(date.getTime())) return isoString;
        const day = String(date.getDate()).padStart(2, "0");
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const year = date.getFullYear();
        let hours = date.getHours();
        const minutes = String(date.getMinutes()).padStart(2, "0");
        const ampm = hours >= 12 ? "PM" : "AM";
        hours = hours % 12 || 12;
        return `${day}/${month}/${year}, ${String(hours).padStart(2, "0")}:${minutes} ${ampm}`;
    };

    const columns = [
        { id: "created_at", label: "Uploaded On", noWrap: true },
        { id: "file_name",  label: "File Name",   width: "260px" },
        { id: "user_id",    label: "Uploaded By",  noWrap: true },
        { id: "total_rows", label: "Total Rows",   noWrap: true },
        { id: "success_count", label: "Success",   noWrap: true },
        { id: "error_count",   label: "Errors",    noWrap: true },
        { id: "status",     label: "Status",       noWrap: true },
        { id: "actions",    label: "Results",       noWrap: true }
    ];

    const sortedHistory = [...history].sort(
        (a, b) => new Date(b.created_at) - new Date(a.created_at)
    );

    // Render-scoped handler so the inline onclick (string-based, per mds cellTemplate
    // convention) can reach back into this render's sortedHistory closure by index.
    window.handleViewReassignRunDetail = (index) => {
        setDetailRecord(sortedHistory[index]);
    };

    const tableData = sortedHistory.map((h, i) => ({
        id: i,
        created_at: formatDate(h.created_at),
        file_name: h.file_name || "-",
        user_id: h.user_id || "-",
        total_rows: h.total_rows,
        success_count: h.success_count,
        error_count: h.error_count,
        status: h.status,
        hasResults: Array.isArray(h.results_json) && h.results_json.length > 0
    }));

    return (
        <div className="bulk-table-wrapper" style={{ marginTop: "28px" }}>
            <h4 className="bulk-card-title" style={{ marginBottom: "16px" }}>Upload History</h4>

            <div style={{
                border: "1px solid #e0e0e0",
                borderRadius: "8px",
                overflow: "hidden",
                display: "flex",
                flexDirection: "column"
            }}>
                <div style={{ flex: 1, minHeight: 0 }}>
                    {loading ? (
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "14px", padding: "40px 0" }}>
                            <div className="loader"></div>
                            <p className="bulk-progress-text" style={{ margin: 0 }}>Loading upload history...</p>
                        </div>
                    ) : (
                        <>
                            <McTable
                                data={tableData}
                                datakey="id"
                                columns={columns.map(col => ({
                                    ...col,
                                    cellTemplate: col.id === "status"
                                        ? (e) => {
                                            const val = e.rowData.status || "";
                                            const ok = val.toLowerCase() === "completed";
                                            const appearance = ok ? "success" : "error";
                                            return e.html`<mc-tag label="${val || "-"}" appearance="${appearance}"></mc-tag>`;
                                        }
                                        : col.id === "actions"
                                        ? (e) => e.rowData.hasResults
                                            ? e.html`<mc-button appearance="secondary" variant="outlined" fit="small" onclick="window.handleViewReassignRunDetail(${e.rowData.id})">View</mc-button>`
                                            : e.html`<span style="color:#9ca3af;font-size:12px;">-</span>`
                                        : undefined
                                }))}
                                zebrastripes
                                headersticky
                                fit="small"
                                height="auto"
                                recordsperpage={tableData.length || 50}
                                style={{ border: "none" }}
                            />
                            {tableData.length === 0 && (
                                <div style={{ padding: "24px", textAlign: "center", color: "#6b7280", fontSize: "13px" }}>
                                    No upload history found.
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>

            <div style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginTop: "16px",
                flexWrap: "wrap",
                gap: "8px"
            }}>
                <span style={{ fontSize: "13px", color: "#6b7280" }}>
                    {totalRecords} total run(s) &middot; Page {page} of {totalPages || 1}
                </span>
                <McPagination
                    currentpage={page}
                    totalpages={totalPages || 1}
                    visiblepages={5}
                    fit="small"
                    hiddenlabel
                    pagechange={(e) => {
                        const newPage = e?.detail?.page;
                        if (newPage && newPage !== page) onPageChange(newPage);
                    }}
                />
            </div>

            <BulkReassignRunDetailModal
                record={detailRecord}
                formatDate={formatDate}
                onClose={() => setDetailRecord(null)}
            />
        </div>
    );
}

/**
 * BulkReassignRunDetailModal Component
 * Shows the per-row results_json for a single historical bulk-reassign run
 */
function BulkReassignRunDetailModal({ record, formatDate, onClose }) {
    const results = record?.results_json || [];

    const columns = [
        { id: "row", label: "Row", noWrap: true },
        { id: "parent_id", label: "Parent ID", noWrap: true },
        { id: "child_id", label: "Child ID", noWrap: true },
        { id: "status", label: "Status", noWrap: true },
        { id: "updated_fields", label: "Updated Fields", width: "320px" },
        { id: "message", label: "Message", width: "280px" }
    ];

    const tableData = results.map((r, i) => ({
        id: i,
        row: r.row,
        parent_id: r.parent_id,
        child_id: r.child_id,
        status: r.status,
        updated_fields: formatUpdatedFields(r.updated_fields),
        message: r.message
    }));

    return (
        <McModal
            className="mds"
            open={!!record}
            heading={record ? `Run Details — ${record.file_name || formatDate(record.created_at)}` : ""}
            width="large"
            closed={onClose}
        >
            {record && (
                <div>
                    <div style={{ display: "flex", gap: "20px", flexWrap: "wrap", marginBottom: "16px", fontSize: "13px", color: "#4a5568" }}>
                        <span><strong>Uploaded On:</strong> {formatDate(record.created_at)}</span>
                        <span><strong>Uploaded By:</strong> {record.user_id || "-"}</span>
                        <span><strong>Total Rows:</strong> {record.total_rows}</span>
                        <span><strong>Success:</strong> {record.success_count}</span>
                        <span><strong>Errors:</strong> {record.error_count}</span>
                    </div>

                    {tableData.length === 0 ? (
                        <div style={{ padding: "24px", textAlign: "center", color: "#6b7280", fontSize: "13px" }}>
                            No detailed row results available for this run.
                        </div>
                    ) : (
                        <div style={{ maxHeight: "480px", overflowY: "auto", border: "1px solid #e0e0e0", borderRadius: "8px" }}>
                            <McTable
                                data={tableData}
                                datakey="id"
                                columns={columns.map(col => ({
                                    ...col,
                                    cellTemplate: col.id === "status"
                                        ? (e) => {
                                            const val = e.rowData.status || "";
                                            const ok = val.includes("success");
                                            const appearance = ok ? "success" : "error";
                                            const label = ok ? "Success" : "Error";
                                            return e.html`<mc-tag label="${label}" appearance="${appearance}"></mc-tag>`;
                                        }
                                        : undefined
                                }))}
                                zebrastripes
                                headersticky
                                fit="small"
                                height="auto"
                                recordsperpage={tableData.length || 25}
                                style={{ border: "none" }}
                            />
                        </div>
                    )}
                </div>
            )}

            <McButton
                slot="secondaryAction"
                appearance="neutral"
                fit="medium"
                dialogaction="cancel"
                click={onClose}
            >
                Close
            </McButton>
        </McModal>
    );
}

export { BulkReassign, BulkReassignResults, BulkReassignHistory };
